import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Mail, Phone, MapPin, Camera, Palette, 
  Linkedin, Instagram, Target, Users, Sparkles
} from 'lucide-react';

const DANIELLE_PHOTO = "https://customer-assets.emergentagent.com/job_legacy-trader/artifacts/32s20zpn_IMG_5921.png";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#0d0c0a] pt-20" data-testid="about-page">
      {/* Hero Section - Tech Artsy Design */}
      <section className="relative min-h-[80vh] overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1c1814] via-[#0d0c0a] to-[#1c1814]" />
          {/* Animated grid lines */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(to right, #D4AF37 1px, transparent 1px),
                linear-gradient(to bottom, #D4AF37 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px'
            }} />
          </div>
          {/* Glowing orbs */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-[#D4AF37]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
                <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-mono">FOUNDER & VISIONARY</span>
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FAFAFA]">
                Danielle<br />
                <span className="text-[#FAFAFA]/80">Courtney Eslyn</span><br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F5E6A3]">
                  Porterfield
                </span>
              </h1>
              
              <p className="font-body text-lg text-[#FAFAFA]/70 max-w-xl leading-relaxed">
                Adaptable and creative leader, versatile in operational development, sales, and marketing. 
                Specialized expertise in formulating strategies, developing client relationships, and 
                leading teams across multiple domains.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => window.open('mailto:Porterfielddanielle22@gmail.com', '_blank')}
                  className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90 font-medium px-6"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Get in Touch
                </Button>
                <Button 
                  variant="outline"
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  onClick={() => window.open('https://www.linkedin.com/in/danielle-porterfield', '_blank')}
                >
                  <Linkedin className="mr-2 h-4 w-4" />
                  LinkedIn
                </Button>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-[#D4AF37]/20">
                <div>
                  <p className="font-display text-3xl text-[#D4AF37]">12+</p>
                  <p className="text-sm text-[#FAFAFA]/50">Years Experience</p>
                </div>
                <div>
                  <p className="font-display text-3xl text-[#D4AF37]">3</p>
                  <p className="text-sm text-[#FAFAFA]/50">Businesses Founded</p>
                </div>
                <div>
                  <p className="font-display text-3xl text-[#D4AF37]">∞</p>
                  <p className="text-sm text-[#FAFAFA]/50">Vision</p>
                </div>
              </div>
            </div>
            
            {/* Right - Profile Image with Tech Frame */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Animated border */}
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D4AF37] via-[#F5E6A3] to-[#D4AF37] rounded-2xl opacity-50 blur-sm animate-pulse" />
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D4AF37] via-[#F5E6A3] to-[#D4AF37] rounded-2xl opacity-20" />
                
                {/* Image container */}
                <div className="relative w-80 h-96 bg-[#1c1814] rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30">
                  <img 
                    src={DANIELLE_PHOTO}
                    alt="Danielle Courtney Eslyn Porterfield"
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1c1814] via-transparent to-transparent" />
                  
                  {/* Tech overlay elements */}
                  <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-[#1c1814]/80 backdrop-blur-sm border border-[#D4AF37]/30 rounded-full">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-xs text-[#FAFAFA] font-mono">ACTIVE</span>
                  </div>
                  
                  {/* Bottom info */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2 text-[#FAFAFA]/80 text-sm">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" />
                      Sandy Springs, GA
                    </div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-6 -right-6 p-4 bg-[#1c1814] border border-[#D4AF37]/30 rounded-xl">
                  <Camera className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <div className="absolute -bottom-6 -left-6 p-4 bg-[#1c1814] border border-[#D4AF37]/30 rounded-xl">
                  <Palette className="h-8 w-8 text-[#D4AF37]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* My Story Section */}
      <section className="py-20 border-t border-[#D4AF37]/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-4xl text-[#FAFAFA] mb-8">
            Building <span className="text-[#D4AF37]">Legacy</span> Through Vision
          </h2>
          
          <div className="space-y-6 text-[#FAFAFA]/70 leading-relaxed text-lg">
            <p>
              My journey began with a camera and a dream. As a graduate of the Art Institute of Atlanta 
              in Commercial Photography, I discovered that capturing moments was just the beginning of 
              my creative evolution.
            </p>
            <p>
              In 2012, I founded <strong className="text-[#D4AF37]">Nouveau Riche Photography</strong>, 
              transforming my passion into a thriving business. As CEO & Chief Creative Officer, I've 
              spent over a decade developing brand identities, turning concepts into reality, and 
              anticipating industry trends before they emerge.
            </p>
            <p>
              Today, my vision has expanded into <strong className="text-[#D4AF37]">Nouveau Riche Legacy</strong> 
              — a comprehensive platform combining financial literacy, trading education, creative services, 
              and entrepreneurial resources. I believe in building generational wealth through knowledge, 
              creativity, and strategic action.
            </p>
            <p>
              Whether I'm leading a retail team to exceed sales goals, designing brand identities, or 
              teaching others about financial markets, my mission remains the same: 
              <strong className="text-[#D4AF37]"> empowering others to build their own legacy.</strong>
            </p>
          </div>
          
          {/* Vision Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="bg-[#1c1814] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all group">
              <CardContent className="p-6">
                <div className="p-3 bg-[#D4AF37]/10 rounded-lg w-fit mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Target className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl text-[#FAFAFA] mb-2">Mission</h3>
                <p className="text-[#FAFAFA]/60 text-sm">
                  To democratize financial education and empower individuals to build generational wealth 
                  through creative entrepreneurship.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1c1814] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all group">
              <CardContent className="p-6">
                <div className="p-3 bg-[#D4AF37]/10 rounded-lg w-fit mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Sparkles className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl text-[#FAFAFA] mb-2">Vision</h3>
                <p className="text-[#FAFAFA]/60 text-sm">
                  A world where creativity and financial literacy intersect, enabling everyone to 
                  transform their passions into sustainable wealth.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1c1814] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all group">
              <CardContent className="p-6">
                <div className="p-3 bg-[#D4AF37]/10 rounded-lg w-fit mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Users className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl text-[#FAFAFA] mb-2">Values</h3>
                <p className="text-[#FAFAFA]/60 text-sm">
                  Authenticity, innovation, continuous learning, and community. Building together 
                  is how we create lasting impact.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Contact CTA */}
      <section className="py-20 border-t border-[#D4AF37]/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl text-[#FAFAFA] mb-4">
            Let's Build <span className="text-[#D4AF37]">Together</span>
          </h2>
          <p className="text-[#FAFAFA]/60 mb-8 max-w-2xl mx-auto">
            Whether you're looking to learn about trading, need creative services, or want to 
            collaborate on building generational wealth, I'd love to connect.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => window.open('mailto:Porterfielddanielle22@gmail.com', '_blank')}
              className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90 px-8"
            >
              <Mail className="mr-2 h-4 w-4" />
              Email Me
            </Button>
            <Button 
              variant="outline"
              className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              onClick={() => window.open('tel:4042598888', '_blank')}
            >
              <Phone className="mr-2 h-4 w-4" />
              (404) 259-8888
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
